import React from 'react';
import { Box, Typography } from '@mui/material';
import { FunnelStage } from './types';

/**
 * The invited → completed funnel.
 *
 * Rendered with styled elements rather than a charting library, for three
 * reasons: recharts v3 renders empty rectangles for a vertical BarChart whose
 * bars are coloured with <Cell> children (verified — the bar groups exist with no
 * path inside); a six-row funnel gains nothing from an axis; and this lets each
 * row carry the one number a bar chart cannot show — how many people were lost
 * since the previous stage, which is the whole point of looking at a funnel.
 */

const TEAL = '#1A5F7A';
const GOLD = '#D4A843';
const GREEN = '#10B981';
const GRAY = '#9CA3AF';

const colorFor = (key: string) => {
  if (key === 'completed') return GREEN;
  if (key === 'viewed') return GOLD;
  if (key === 'invited') return GRAY;
  return TEAL;
};

interface Props {
  stages: FunnelStage[];
  bounced?: number;
}

export const FunnelChart: React.FC<Props> = ({ stages, bounced = 0 }) => {
  const top = stages[0]?.count || 0;

  return (
    <Box>
      {stages.map((s, i) => {
        const width = top > 0 ? (s.count / top) * 100 : 0;
        const prev = i > 0 ? stages[i - 1].count : null;
        const lost = prev !== null ? prev - s.count : 0;
        const stepPct = prev && prev > 0 ? Math.round((s.count / prev) * 100) : null;

        return (
          <Box key={s.key} sx={{ mb: 1.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#111827',
                  minWidth: 168,
                }}
              >
                {s.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#111827',
                  minWidth: 44,
                }}
              >
                {s.count}
              </Typography>
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6B7280' }}>
                {s.pct_of_invited}% of invited
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              {lost > 0 && (
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#7D1935', fontWeight: 600 }}>
                  −{lost} lost here
                </Typography>
              )}
              {stepPct !== null && lost === 0 && (
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#10B981', fontWeight: 600 }}>
                  no drop-off
                </Typography>
              )}
            </Box>

            <Box sx={{ position: 'relative', height: 26, borderRadius: '6px', bgcolor: '#F3F4F6', overflow: 'hidden' }}>
              <Box
                sx={{
                  width: `${width}%`,
                  height: '100%',
                  bgcolor: colorFor(s.key),
                  borderRadius: '6px',
                  transition: 'width .35s ease',
                }}
              />
              {stepPct !== null && s.count > 0 && (
                <Typography
                  sx={{
                    position: 'absolute',
                    left: 10,
                    top: 0,
                    lineHeight: '26px',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#fff',
                    // Only sits inside the bar when there is room for it.
                    display: width > 12 ? 'block' : 'none',
                  }}
                >
                  {stepPct}% of previous
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}

      {bounced > 0 && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #F3F4F6' }}>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#7D1935' }}>
            {bounced} address{bounced === 1 ? '' : 'es'} bounced and {bounced === 1 ? 'is' : 'are'} excluded from
            every stage above — they were never reachable.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FunnelChart;
